import React, { useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as searchInvoiceMetaData from '../../actions/invoiceDateActions';
import SystemNotesTable from './SystemNotesTable';
import { 
  FileText, User, MapPin, Clock, CheckCircle,
  Phone, Info, Calendar, Truck, AlertCircle,
  Loader, Car, Hash, Palette, Key, CreditCard,
  Package, Unlock, UserCheck, ArrowRightCircle
} from 'lucide-react';

const VendorInvoiceView = ({ fetchInvoice, invoiceDetails, saveNotes, invoicenotes, match }) => {
  const [activeTab, setActiveTab] = useState('invoice');
  const [isSaving, setIsSaving] = useState(false);
  const invoiceId = match.params.invoiceId;

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice(invoiceId);
    }
  }, [invoiceId, fetchInvoice]);

  const handleSaveNotes = async (obj) => {
    setIsSaving(true);
    try {
      await saveNotes(obj, invoiceId);
    } finally {
      setTimeout(() => {
        setIsSaving(false);
      }, 1000);
    }
  };

  const HeaderTab = ({ text, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`
        relative px-8 py-4 text-lg font-medium transition-all
        ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}
        focus:outline-none
      `}
    >
      {text}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-md"></div>
      )}
    </button>
  );

  const DataItem = ({ icon: Icon, label, value, variant = 'default' }) => {
    const variants = {
      default: 'bg-white hover:bg-gray-50',
      primary: 'bg-blue-50 hover:bg-blue-100',
      success: 'bg-green-50 hover:bg-green-100',
      warning: 'bg-amber-50 hover:bg-amber-100',
      purple: 'bg-purple-50 hover:bg-purple-100'
    };

    const iconVariants = {
      default: 'bg-gray-100 text-gray-600',
      primary: 'bg-blue-100 text-blue-600',
      success: 'bg-green-100 text-green-600',
      warning: 'bg-amber-100 text-amber-600',
      purple: 'bg-purple-100 text-purple-600'
    };

    const textVariants = {
      default: 'text-gray-900',
      primary: 'text-blue-900',
      success: 'text-green-900',
      warning: 'text-amber-900',
      purple: 'text-purple-900'
    };

    return (
      <div className={`rounded-lg p-6 transition-colors ${variants[variant]}`}>
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-lg ${iconVariants[variant]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="text-base text-gray-500">{label}</div>
            <div className={`font-medium text-lg ${textVariants[variant]}`}>
              {value || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ──────── Release Status Badge ────────
  const ReleaseStatusBadge = ({ status }) => {
    const isReleased = status && status.toLowerCase() === 'released';
    return (
      <div className={`
        inline-flex items-center px-4 py-2 rounded-full text-base font-medium
        ${isReleased 
          ? 'bg-green-100 text-green-700' 
          : 'bg-amber-100 text-amber-700'}
      `}>
        {isReleased 
          ? <CheckCircle className="w-5 h-5 mr-2" /> 
          : <Package className="w-5 h-5 mr-2" />}
        {status || 'In Storage'}
      </div>
    );
  };

  const TimelineItem = ({ title, time, isComplete = true, isLast = false }) => (
    <div className="flex relative pb-10">
      <div className="absolute left-5 top-6 bottom-0">
        {!isLast && isComplete && (
          <div className="w-0.5 h-full bg-green-200"/>
        )}
      </div>
      <div className={`
        w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10
        ${isComplete ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
      `}>
        <Clock className="w-5 h-5" />
      </div>
      <div className="ml-6 flex-1">
        <div className="text-base font-medium text-gray-900">{title}</div>
        <div className="text-base text-gray-500">{time || 'Pending'}</div>
      </div>
    </div>
  );

  const SectionCard = ({ title, icon: Icon, children, className = '', headerRight }) => (
    <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${className}`}>
      <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Icon className="w-6 h-6 text-gray-400" />
          <h3 className="font-medium text-xl text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center space-x-3">
          {headerRight}
          {title === "Service Notes" && isSaving && (
            <div className="flex items-center text-blue-600 space-x-3">
              <Loader className="w-5 h-5 animate-spin" />
              <span className="text-base">Saving...</span>
            </div>
          )}
        </div>
      </div>
      <div className="p-8">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-9xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm mb-8">
          <div className="p-8 border-b border-gray-100">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">
                    Invoice #{invoiceDetails.invoiceId}
                  </h1>
                  <p className="text-gray-500 mt-2 text-lg">
                    Created on {invoiceDetails.invoiceCreatedDate}
                  </p>
                </div>
              </div>
              <div className="flex items-center px-6 py-3 rounded-full bg-green-100 text-green-700 text-lg">
                <CheckCircle className="w-5 h-5 mr-3" />
                Active
              </div>
            </div>
          </div>
          
          <div className="px-8 border-b border-gray-100">
            <HeaderTab 
              text="Invoice Details" 
              isActive={activeTab === 'invoice'} 
              onClick={() => setActiveTab('invoice')} 
            />
            <HeaderTab 
              text="Notes" 
              isActive={activeTab === 'notes'} 
              onClick={() => setActiveTab('notes')} 
            />
          </div>
        </div>

        {activeTab === 'invoice' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* ──────── Service Information ──────── */}
              <SectionCard title="Service Information" icon={Truck}>
                <div className="grid gap-6">
                  <DataItem 
                    icon={FileText} 
                    label="Invoice Number" 
                    value={invoiceDetails.invoiceId} 
                    variant="primary"
                  />
                  <DataItem 
                    icon={User} 
                    label="Account Name" 
                    value={invoiceDetails.name}
                  />
                  <DataItem 
                    icon={Phone} 
                    label="Contact" 
                    value={invoiceDetails.callerName}
                  />
                  <DataItem 
                    icon={Info} 
                    label="Service Reason" 
                    value={invoiceDetails.reason}
                  />
                </div>
              </SectionCard>

              {/* ──────── NEW: Vehicle Information ──────── */}
              <SectionCard title="Vehicle Information" icon={Car}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DataItem 
                    icon={Calendar} 
                    label="Year" 
                    value={invoiceDetails.vehicle_Year}
                    variant="purple"
                  />
                  <DataItem 
                    icon={Car} 
                    label="Make" 
                    value={invoiceDetails.vehicle_Make}
                    variant="purple"
                  />
                  <DataItem 
                    icon={Truck} 
                    label="Model" 
                    value={invoiceDetails.vehicle_Model}
                    variant="purple"
                  />
                  <DataItem 
                    icon={Palette} 
                    label="Color" 
                    value={invoiceDetails.vehicle_Color}
                    variant="purple"
                  />
                  <DataItem 
                    icon={Key} 
                    label="VIN" 
                    value={invoiceDetails.vehicle_VIN}
                  />
                  <DataItem 
                    icon={CreditCard} 
                    label="Plate" 
                    value={invoiceDetails.vehicle_Plate_No}
                  />
                  <DataItem 
                    icon={Hash} 
                    label="OR/DR Number" 
                    value={invoiceDetails.vehicle_OR_DR_No}
                  />
                </div>
              </SectionCard>

              {/* ──────── Location Details ──────── */}
              <SectionCard title="Location Details" icon={MapPin}>
                <div className="space-y-6">
                  <DataItem 
                    icon={MapPin} 
                    label="Pickup Location" 
                    value={invoiceDetails.pickUpAddress} 
                    variant="primary"
                  />
                  <DataItem 
                    icon={MapPin} 
                    label="Drop Location" 
                    value={invoiceDetails.dropOffAddress} 
                    variant="primary"
                  />
                </div>
              </SectionCard>
            </div>

            <div className="lg:col-span-1 space-y-8">
              {/* ──────── NEW: Release Information (only shown for storage tows) ──────── */}
              {invoiceDetails.releaseStatus && (
              <SectionCard 
                title="Release Information" 
                icon={Unlock}
                headerRight={<ReleaseStatusBadge status={invoiceDetails.releaseStatus} />}
              >
                {invoiceDetails.releaseStatus === 'Released' ? (
                <div className="space-y-6">
                  <DataItem 
                    icon={MapPin} 
                    label="Lot Location" 
                    value={invoiceDetails.dropOffLocation}
                    variant="success"
                  />
                  <DataItem 
                    icon={Clock} 
                    label="Release Date / Time" 
                    value={invoiceDetails.releaseDateTime}
                    variant={invoiceDetails.releaseDateTime ? 'success' : 'default'}
                  />
                  <DataItem 
                    icon={UserCheck} 
                    label="Released By" 
                    value={invoiceDetails.releasedName}
                    variant={invoiceDetails.releasedName ? 'success' : 'default'}
                  />
                  <DataItem 
                    icon={ArrowRightCircle} 
                    label="Released To" 
                    value={invoiceDetails.releasedTo}
                    variant={invoiceDetails.releasedTo ? 'success' : 'default'}
                  />
                </div>
                ) : (
                <div className="space-y-6">
                  <DataItem 
                    icon={MapPin} 
                    label="Lot Location" 
                    value={invoiceDetails.dropOffLocation}
                    variant="warning"
                  />
                </div>
                )}
              </SectionCard>
              )}

              {/* ──────── Service Timeline ──────── */}
              <SectionCard title="Service Timeline" icon={Calendar}>
                <div className="space-y-2">
                  <TimelineItem 
                    title="Request Received" 
                    time={invoiceDetails.requestTime}
                  />
                  <TimelineItem 
                    title="Dispatched" 
                    time={invoiceDetails.dispatchTime}
                  />
                  <TimelineItem 
                    title="Service Accepted" 
                    time={invoiceDetails.acceptedTime} 
                    isComplete={!!invoiceDetails.acceptedTime}
                  />
                  <TimelineItem 
                    title="En Route" 
                    time={invoiceDetails.enrouteTime}
                    isComplete={!!invoiceDetails.enrouteTime}
                  />
                  <TimelineItem 
                    title="Arrived at Location" 
                    time={invoiceDetails.arrivedTime}
                    isComplete={!!invoiceDetails.arrivedTime}
                  />
                  <TimelineItem 
                    title="Service Started" 
                    time={invoiceDetails.hookedTime}
                    isComplete={!!invoiceDetails.hookedTime}
                  />
                  <TimelineItem 
                    title="Drop Off" 
                    time={invoiceDetails.dropOffTime}
                    isComplete={!!invoiceDetails.dropOffTime}
                  />
                  <TimelineItem 
                    title="Service Cleared" 
                    time={invoiceDetails.clearedTime}
                    isComplete={!!invoiceDetails.clearedTime}
                    isLast
                  />
                </div>
              </SectionCard>
            </div>
          </div>
        ) : (
          <SectionCard title="Service Notes" icon={FileText}>
            <SystemNotesTable 
              notesData={invoicenotes} 
              invoiceId={invoiceId} 
              saveNotes={handleSaveNotes}
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  invoiceDetails: (state.invoiceList && state.invoiceList.invoiceDetails) || {},
  invoicenotes: (state.invoiceList && state.invoiceList.invoicenotes) || [],
  authInfo: state.authInfo,
});

const mapDispatchToProps = (dispatch) => ({
  fetchInvoice: (id) => {
    dispatch(searchInvoiceMetaData.fetchInvoice(id));
    dispatch(searchInvoiceMetaData.fetchInvoiceNotes(id));
  },
  saveNotes: async (obj, id) => {
    await dispatch(searchInvoiceMetaData.saveNotes(obj, id));
  }
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VendorInvoiceView));